import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Drawer,
  TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Divider, CircularProgress, Alert, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { apiFetch } from '../api';

const AccountMaster = () => {
  const [accounts, setAccounts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [error, setError] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    group_id: '',
    opening_balance: 0
  });

  const fetchData = async () => {
    setLoading(true);
    try {
        const accRes = await apiFetch('/api/accounts');
        const accData = await accRes.json();
        setAccounts(accData);

        const grpRes = await apiFetch('/api/account-groups');
        const grpData = await grpRes.json();
        setGroups(grpData);
    } catch (err) {
        setError('डेटा लोड करताना त्रुटी आली.');
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openDrawerForCreate = () => {
    setEditingId(null);
    setFormData({ name: '', group_id: '', opening_balance: 0 });
    setError('');
    setDrawerOpen(true);
  };

  const handleEdit = (row) => {
    // Only manual accounts should be editable if they are not linked to customer/supplier
    if (row.linked_table) {
        alert('हे खाते लिंक केलेले आहे (ग्राहक/व्यापारी), त्यामुळे ते इथून बदलता येणार नाही.');
        return;
    }
    setEditingId(row.id);
    setFormData({
      name: row.name,
      group_id: row.group_id,
      opening_balance: row.opening_balance || 0
    });
    setError('');
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.group_id) {
        setError('कृपया सर्व माहिती भरा.');
        return;
    }

    setLoading(true);
    const endpoint = editingId ? `/api/accounts/${editingId}` : '/api/accounts';
    const method = editingId ? 'PUT' : 'POST';

    try {
        const response = await apiFetch(endpoint, {
            method,
            body: JSON.stringify(formData)
        });

        if (response.ok) {
            setDrawerOpen(false);
            setEditingId(null);
            setFormData({ name: '', group_id: '', opening_balance: 0 });
            fetchData();
        } else {
            setError('माहिती जतन करताना अडचण आली.');
        }
    } catch (err) {
        setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
        setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
          खाते मास्टर (Account Master)
        </Typography>
        <Button
          variant="contained"
          onClick={openDrawerForCreate}
          sx={{ backgroundColor: '#0FA958' }}
        >
          + नवीन खाते जोडा
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        येथे तुम्ही तुमचे सर्व लेजर अकाऊंट्स (रोख, बँक, खर्च, उत्पन्न) पाहू शकता. ग्राहक आणि व्यापाऱ्यांची खाती आपोआप तयार होतात.
      </Alert>

      <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: '70vh', overflow: 'auto' }}>
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: '#f0fdf4' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>अ. क्र.</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>खात्याचे नाव</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>ग्रुप</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>आरंभीची शिल्लक (₹)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>चालू शिल्लक (₹)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>अॅक्शन</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
                <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : accounts.map((row, index) => (
              <TableRow key={row.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {row.name} {row.linked_table && <Chip label={row.linked_table} size="small" variant="outlined" sx={{ ml: 1, fontSize: '0.7rem' }} />}
                    </Typography>
                </TableCell>
                <TableCell>{row.group?.name}</TableCell>
                <TableCell align="right">₹ {row.opening_balance}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹ {row.current_balance}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEdit(row)} sx={{ color: '#1B5E20' }} disabled={row.linked_table}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 400 } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {editingId ? 'खाते संपादन' : 'नवीन खाते जोडा'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <Divider sx={{ mb: 3 }} />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <TextField fullWidth label="खात्याचे नाव" variant="outlined" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} sx={{ mb: 2.5 }} />
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>ग्रुप</InputLabel>
            <Select label="ग्रुप" value={formData.group_id} onChange={(e) => setFormData({...formData, group_id: e.target.value})}>
                {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField fullWidth label="आरंभीची शिल्लक (₹)" type="number" variant="outlined" value={formData.opening_balance} onChange={(e) => setFormData({...formData, opening_balance: e.target.value})} sx={{ mb: 2.5 }} />

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button fullWidth variant="outlined" onClick={() => setDrawerOpen(false)}>रद्द करा</Button>
            <Button fullWidth variant="contained" onClick={handleSave} sx={{ backgroundColor: '#0FA958' }}> जतन करा </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default AccountMaster;
